import useSettings from "features/settings/useSettings";
import { createContext, useContext, useEffect, useMemo } from "react";
import {
  PivotReportDataContextType,
  usePivotReportDataContext,
} from "./usePivotReportData";

export type PivotReportParamsType = {
  rowDimension?: string;
  columnDimension?: string;
  primaryMeasure?: string;
  secondaryMeasure?: string;
  heatMapEnabled: boolean;
  showTotals: boolean;
};

export type PivotReportContextType = {
  params: PivotReportParamsType;
  setParams: (params: Partial<PivotReportParamsType>) => void;
  heatMapColors: {
    getColor: (value: number, measure: string) => string;
    getIntensity: (value: number, max: number, min: number) => number;
  };
  load: () => Promise<void>;
  loading: boolean;
} & Omit<PivotReportDataContextType, "load" | "loading">;

export const PivotReportContext = createContext<PivotReportContextType | null>(
  null,
);

export interface UsePivotReportProps extends Partial<PivotReportParamsType> {}

export default function usePivotReport(props?: UsePivotReportProps) {
  const { data, dataReportType, filter, setFilter, load, loading, excelUrl } =
    usePivotReportDataContext();

  // Get settings for pivot report
  const [allParams, setAllParamsInternal] = useSettings({ key: "pivotReport" });

  // Get current report type params or initialize with defaults
  const params: PivotReportParamsType = allParams?.[filter.reportType] || {
    heatMapEnabled: true,
    showTotals: true,
  };

  // Function to update params for current report type
  const setParamsInternal = (newParams: Partial<PivotReportParamsType>) => {
    setAllParamsInternal({
      ...allParams,
      [filter.reportType]: {
        ...params,
        ...newParams,
      },
    });
  };

  // Populate params with defaults from config if they are not set.
  // Guard: only apply when the loaded data belongs to the current reportType,
  // so we never apply stale defaults from a previously loaded type.
  useEffect(() => {
    if (data?.config?.defaults && dataReportType === filter.reportType) {
      const needsDefaults =
        !params.rowDimension ||
        !params.columnDimension ||
        !params.primaryMeasure;
      if (needsDefaults) {
        const newRowDimension =
          params.rowDimension || data.config.defaults.row_dimension;
        const newColumnDimension =
          params.columnDimension || data.config.defaults.column_dimension;
        const newPrimaryMeasure =
          params.primaryMeasure || data.config.defaults.primary_measure;
        const newSecondaryMeasure =
          params.secondaryMeasure || data.config.defaults.secondary_measure;

        setParamsInternal({
          ...params,
          rowDimension: newRowDimension,
          columnDimension: newColumnDimension,
          primaryMeasure: newPrimaryMeasure,
          secondaryMeasure: newSecondaryMeasure,
        });
        setFilter({
          ...filter,
          rowDimension: newRowDimension,
          columnDimension: newColumnDimension,
          primaryMeasure: newPrimaryMeasure,
          secondaryMeasure: newSecondaryMeasure,
        });
      }
    }
  }, [
    dataReportType,
    data?.config?.defaults,
    params.rowDimension,
    params.columnDimension,
    params.primaryMeasure,
  ]);

  const setParams = (newParams: Partial<PivotReportParamsType>) => {
    setParamsInternal({
      ...params,
      ...newParams,
    });

    // Update filter if dimensions changed
    if (
      newParams.rowDimension ||
      newParams.columnDimension ||
      newParams.primaryMeasure ||
      newParams.hasOwnProperty("secondaryMeasure")
    ) {
      setFilter({
        ...filter,
        rowDimension: newParams.rowDimension || params.rowDimension,
        columnDimension: newParams.columnDimension || params.columnDimension,
        primaryMeasure: newParams.primaryMeasure || params.primaryMeasure,
        secondaryMeasure: newParams.hasOwnProperty("secondaryMeasure")
          ? newParams.secondaryMeasure
          : params.secondaryMeasure,
      });
    }
  };

  const heatMapColors = useMemo(() => {
    const getUtilizationColor = (value: number) => {
      if (value <= 50) return "#c8e6c9"; // Low - Green
      if (value <= 75) return "#fff3e0"; // Medium - Orange
      if (value <= 90) return "#ffcdd2"; // High - Light Red
      return "#d32f2f"; // Critical - Dark Red
    };

    const getRevenueColor = (value: number, max: number) => {
      const intensity = value / max;
      if (intensity <= 0.25) return "#e8f5e8";
      if (intensity <= 0.5) return "#c8e6c9";
      if (intensity <= 0.75) return "#81c784";
      return "#4caf50";
    };

    const getColor = (value: number, measure: string, max?: number) => {
      if (measure === "utilization") {
        return getUtilizationColor(value);
      } else if (
        measure === "revenue" ||
        measure === "avg_price" ||
        measure === "avg_price_per_guest"
      ) {
        return getRevenueColor(value, max || 100);
      } else {
        // For count-based measures (booking_count, booked_guests, capacity)
        const intensity = max ? value / max : 0.5;
        const alpha = Math.max(0.1, Math.min(0.8, intensity));
        return `rgba(33, 150, 243, ${alpha})`; // Blue with varying opacity
      }
    };

    const getIntensity = (value: number, max: number, min: number) => {
      if (max === min) return 0.5;
      return (value - min) / (max - min);
    };

    return {
      getColor,
      getIntensity,
    };
  }, []);

  return {
    data,
    dataReportType,
    filter,
    setFilter,
    params: {
      rowDimension: params.rowDimension,
      columnDimension: params.columnDimension,
      primaryMeasure: params.primaryMeasure,
      secondaryMeasure: params.secondaryMeasure,
      heatMapEnabled: params.heatMapEnabled ?? true,
      showTotals: params.showTotals ?? true,
      ...props,
    },
    setParams,
    heatMapColors,
    load,
    loading,
    excelUrl,
  };
}

export function usePivotReportContext() {
  const context = useContext(PivotReportContext);
  if (!context) {
    throw Error(
      "usePivotReportContext cannot be used outside of PivotReportProvider.",
    );
  }
  return context;
}
